import React, { useMemo, useState } from 'react';
import { Formik, Form, FieldArray } from 'formik';
import { post } from 'axios';

import { actionSvgs } from 'assets/icons';

import FilledButton from 'components/Buttons/FilledButton';
import ModalContainer from 'components/ModalContainer';

import { renderToast } from 'components/Informations/Toast/utils';

import endpoints from 'services/endpoints';
import useFetch from 'hooks/useFetch';

import * as Yup from 'yup';
import * as S from './styles';

const Add = () => actionSvgs('add');
const Delete = () => actionSvgs('remove');

const { getQuestionTypes, insertQuestion } = endpoints;

export default function SoftSkillAddQuestion({ data, open, setOpen, type }) {
  const { id_area, id_funcionario: idFuncionario, observer } = data;

  const [toastVisibility, setToastVisibility] = useState(false);
  const [toastInfos, setToastInfos] = useState({
    type: '',
    message: '',
    autoClose: 6000,
  });
  const [questionType, setQuestionType] = useState(data?.level || NaN);

  const [responseGetOpts] = useFetch(getQuestionTypes);

  const initialValues = useMemo(
    () => ({
      block: data?.selected?.value || NaN,
      question: data?.question || null,
      id_tipo_questao: questionType,
      description: data?.description || '',
      alternatives:
        questionType <= 3 ? data?.alternatives || [{ descricao: '' }] : [],
    }),
    [questionType, data]
  );

  const schema = useMemo(() => {
    const reqMsg = 'Campo Obrigatório';
    const yupSchema = {
      block: Yup.number().required(reqMsg),
      question: Yup.string()
        .min(3, reqMsg)
        .required(reqMsg),
      id_tipo_questao: Yup.number()
        .typeError('')
        .required(reqMsg),
      description: Yup.lazy(value => {
        if (value?.length) {
          return Yup.string()
            .min(3, reqMsg)
            .required();
        }
        return Yup.string().nullable();
      }),
      alternatives: Yup.lazy(value => {
        if (questionType <= 3 || value?.length) {
          return Yup.array().of(
            Yup.object().shape({
              descricao: Yup.string()
                .min(3, reqMsg)
                .required(reqMsg),
            })
          );
        }
        return Yup.array().nullable();
      }),
    };

    return Yup.object().shape(yupSchema);
  }, [questionType]);

  const handleSubmit = (values, { resetForm }) => {
    const objectToSend = {
      id_usr_in: idFuncionario,
      id_area,
      id_bloco: Number(values.block),
      id_tipo_questao: values.id_tipo_questao,
      nome: values.question,
      descricao: values.description,
    };

    return post(insertQuestion, objectToSend)
      .then(() => {
        setToastVisibility(true);
        setToastInfos({
          type: 'success',
          message: 'Questão atualizada com sucesso',
          autoClose: 6000,
        });
      })
      .then(() => {
        setTimeout(() => {
          resetForm();
          observer();
          setOpen(false);
        }, 4000);
      })
      .catch(() => {
        setToastVisibility(true);
        setToastInfos({
          type: 'error',
          message: 'Erro ao atualizar questão',
          autoClose: 6000,
        });
      });
  };

  const RenderForm = useMemo(
    () => (
      <Formik
        initialValues={initialValues}
        validationSchema={schema}
        onSubmit={handleSubmit}
      >
        {({ values, errors, setFieldValue, resetForm }) => (
          <Form>
            <S.FormField
              label="Bloco"
              label_for="bloco_select"
              error={!!errors?.block}
              error_message={errors?.block}
            >
              <S.SelectField
                id="bloco_select"
                defaultValue={data?.selected}
                value={values?.block}
                onChange={e => setFieldValue('block', e.target.value)}
                error={!!errors?.block}
              >
                <option>Selecione um bloco...</option>
                {data?.options
                  ?.filter(i => !i.label?.match(/ALL - Idiomas/g))
                  ?.map(item => (
                    <option value={item.value}>{item.label}</option>
                  ))}
              </S.SelectField>
            </S.FormField>
            <S.FormField
              label="Ferramenta/Tecnologia/Metodologia"
              label_for="question_name"
            >
              <S.FormInput
                id="question_name"
                name="question"
                value={values.question}
                onChange={e => {
                  setFieldValue('question', e.target.value);
                }}
              />
            </S.FormField>
            <S.FormField
              label="Tipo de questão"
              label_for="questionType"
              error={!!errors?.id_tipo_questao}
              error_message={errors?.id_tipo_questao}
            >
              <S.SelectField
                id="questionType"
                name="questionType"
                defaultValue={data?.level}
                value={values?.id_tipo_questao}
                onChange={e => {
                  setFieldValue('id_tipo_questao', parseInt(e.target.value));
                  setQuestionType(parseInt(e.target.value));
                }}
                hasAlert={questionType <= 3 && values.alternatives?.length}
                disabled={questionType <= 3 && values.alternatives?.length}
                error={!!errors?.id_tipo_questao}
              >
                <option>Selecione o tipo...</option>
                {responseGetOpts?.map((opt, k) => (
                  <option value={opt?.id_tipo_questao} key={k}>
                    {opt.descricao}
                  </option>
                ))}
              </S.SelectField>
            </S.FormField>
            <S.FormField
              label="Descrição"
              label_for="description"
              error={!!errors?.description}
              error_message={errors?.description}
            >
              <S.TextField
                id="description"
                name="description"
                error={!!errors?.description}
                onChange={e => setFieldValue('description', e.target.value)}
              />
            </S.FormField>
            {questionType <= 3 && (
              <FieldArray
                name="alternatives"
                render={arrHelepers =>
                  values?.alternatives?.length ? (
                    values?.alternatives?.map((item, k) => (
                      <S.FormRow key={k}>
                        <S.FormField
                          label={k === 0 && 'Alternativas'}
                          label_for={`alternative_${k}`}
                          error={
                            !!errors.alternatives?.length &&
                            errors.alternatives[k]?.descricao
                          }
                          error_message={
                            !!errors.alternatives?.length &&
                            errors.alternatives[k]?.descricao
                          }
                        >
                          <S.FormInput
                            id={`alternative_${k}`}
                            value={item?.descricao}
                            onChange={e =>
                              setFieldValue(
                                `alternatives[${k}].descricao`,
                                e.target.value
                              )
                            }
                            error={
                              !!errors.alternatives?.length &&
                              errors.alternatives[k]?.descricao
                            }
                          />
                        </S.FormField>
                        <S.ButtonsWrap>
                          <S.FormButton
                            className="icon"
                            icon={Delete}
                            onClick={e => {
                              e.preventDefault();
                              arrHelepers.remove(k);
                            }}
                          />
                          {values.alternatives?.length - 1 === k && (
                            <S.FormButton
                              className="icon"
                              kind="primary"
                              icon={Add}
                              onClick={e => {
                                e.preventDefault();
                                arrHelepers.insert(k + 1, { descricao: '' });
                              }}
                            />
                          )}
                        </S.ButtonsWrap>
                      </S.FormRow>
                    ))
                  ) : (
                    <FilledButton
                      buttonType="default"
                      text="Adicionar Alternativa"
                      handleClick={() => arrHelepers.push({ descricao: '' })}
                    />
                  )
                }
              />
            )}

            <S.ButtonsWrap>
              <FilledButton
                text="Cancelar"
                buttonClass="cancel"
                handleClick={e => {
                  e.preventDefault();
                  resetForm();
                  setOpen(false);
                }}
              />
              <FilledButton
                text="enviar"
                buttonType="submit"
                buttonClass="confirm"
              />
            </S.ButtonsWrap>
          </Form>
        )}
      </Formik>
    ),
    [initialValues, schema, responseGetOpts, data, questionType]
  );

  return (
    <>
      {toastVisibility &&
        renderToast(
          toastInfos.type,
          toastInfos.message,
          toastInfos.autoClose,
          setToastVisibility
        )}

      <ModalContainer isOpen={open} setIsOpen={setOpen} headText="Soft Skills">
        {RenderForm}
      </ModalContainer>
    </>
  );
}
